/**
 * A área da conclusão da mesa única (E201.F1, GUS-47, ADR-0144): a conclusão da CCB, a formalização
 * preparada, a conferência no portal e o detalhe por documento. E205.F1 (ADR-0147): é o conteúdo da
 * aba Conclusão; a área do exame foi para `AreaDoExame.tsx` na E205.F2.
 */
import type { AxiosInstance } from 'axios';

import { ConferenciaNoPortal } from '@/components/ConferenciaNoPortal/ConferenciaNoPortal';
import { FormalizacaoPreparada } from '@/components/Formalizacao/FormalizacaoPreparada';
import { ConclusaoDaCcb } from '@/components/MesaEletronica/ConclusaoDaCcb';
import { DetalhePorDocumento } from '@/components/MesaEletronica/DetalhePorDocumento';
import type { useMesaEletronica } from '@/components/MesaEletronica/useMesaEletronica';
import { ptBR } from '@/i18n/pt-BR';
import { ancoraDaOrigem, type DocumentoDaSaidaData } from '@/lib/documentoDaSaida';
import { exigeConclusao } from '@/lib/exame_eletronico_ccb';

import { NoDocumento } from './NoDocumento';

const tn = ptBR.mesaUnica.noDocumento;

type Mesa = ReturnType<typeof useMesaEletronica>;

export interface AreaDaConclusaoProps {
  client: AxiosInstance;
  caseId: string;
  mesa: Mesa;
  onMudou: () => void;
  onAbrirDocumentos: () => void;
  /** E203.F5.T09: o documento do centro, para o aviso que leva à seção onde o dado está. */
  documento: DocumentoDaSaidaData | null;
}

/** E209.F2.T01: o resumo da aba Conclusão; a CCB sem conclusão registrada é facultativa (GUS-254). */
export function resumoDaConclusao(pendentes: number): string {
  if (pendentes === 0) return tn.conclusaoFeita;
  if (pendentes === 1) return tn.umaConclusaoPendente;
  return tn.conclusaoPendente.replace('{n}', String(pendentes));
}

export function AreaDaConclusao(props: AreaDaConclusaoProps): JSX.Element {
  const { client, caseId, mesa, onMudou } = props;
  const decisoes = {
    acaoPorCcb: mesa.acaoPorCcb,
    erroPorCcb: mesa.erroPorCcb,
    sucessoPorCcb: mesa.sucessoPorCcb,
    onConcluir: mesa.concluir,
    onReabrir: mesa.reabrir,
    onMudou,
  };
  const ccbs = mesa.ccbs;
  const aConcluir = (ccbs ?? [])
    .filter(exigeConclusao)
    .filter((c) => c.exame?.status !== 'concluido').length;
  return (
    <div data-testid="mesa-area-conclusao">
      {ccbs !== null && ccbs.length > 0 ? (
        <NoDocumento
          id="conclusao-da-ccb"
          resumo={resumoDaConclusao(aConcluir)}
          pendente={aConcluir > 0}
          ancora={ancoraDaOrigem(props.documento, (o) => o.tipo === 'conclusao')}
        >
          <ConclusaoDaCcb
            client={client}
            caseId={caseId}
            pecas={ccbs}
            todasConcluidas={mesa.todasConcluidas}
            {...decisoes}
          />
        </NoDocumento>
      ) : null}
      <FormalizacaoPreparada
        client={client}
        caseId={caseId}
        releitura={ccbs}
        noDocumento={{ ancora: ancoraDaOrigem(props.documento, (o) => o.revisao !== null) }}
      />
      <ConferenciaNoPortal client={client} caseId={caseId} onRegistrada={onMudou} numaLinha />
      {ccbs !== null && ccbs.length > 0 ? (
        <DetalhePorDocumento
          client={client}
          caseId={caseId}
          protocolo={mesa.protocolo}
          pecas={ccbs}
          onAbrirPreliminar={props.onAbrirDocumentos}
          {...decisoes}
        />
      ) : null}
    </div>
  );
}
